import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { DEFAULT_DASHBOARD_LAYOUT_SL } from '~/constants/constants';

/**
 * Creates a new config that resets widgets to their default positions
 * and adds any missing widgets from the default layout
 * @param currentConfig - Current dashboard configuration
 * @param defaultLayout - Default layout configuration to reset to
 * @returns Updated configuration with widgets reset to default positions
 */
export const resetToDefaultLayout = (
  currentConfig: Config[],
  defaultLayout: Config[] = DEFAULT_DASHBOARD_LAYOUT_SL,
): Config[] => {
  // Sort default layout by ID length (longest first) to match more specific patterns first
  // e.g., 'allocation-table-universe' matches before 'allocation-table'
  const sortedDefaultLayout = [...defaultLayout].sort(
    (a, b) => (b.id?.length || 0) - (a.id?.length || 0),
  );

  // Convert old allocation widgets to allocation-table-universe if default layout expects it
  // This handles migration of old upload dashboards
  const hasAllocationTableUniverse = sortedDefaultLayout.some(
    (item) => item.id === 'allocation-table-universe',
  );
  const convertedConfig = currentConfig.map((currentWidget) => {
    // If default layout has allocation-table-universe and we have an old allocation widget, convert it
    if (
      hasAllocationTableUniverse &&
      currentWidget.id.includes('allocation') &&
      !currentWidget.id.includes('allocation-table') &&
      !currentWidget.id.includes('sector-allocation')
    ) {
      return {
        ...currentWidget,
        id: currentWidget.id.replace(
          /allocation(-widget-|$)/,
          'allocation-table-universe$1',
        ),
      };
    }
    return currentWidget;
  });

  // Count how many of each widget type should exist in the default layout
  const widgetTypeCounts = new Map<string, number>();
  sortedDefaultLayout.forEach((defaultItem) => {
    const count = widgetTypeCounts.get(defaultItem.id) || 0;
    widgetTypeCounts.set(defaultItem.id, count + 1);
  });

  // Track how many of each widget type we've added
  const addedWidgetCounts = new Map<string, number>();

  // Update existing widgets to their default positions
  // Filter out widgets that don't match any default layout item
  // Only keep the expected number of each widget type
  const updatedConfig: Config[] = [];
  convertedConfig.forEach((currentWidget) => {
    // Find the most specific match (sorted list ensures longest match comes first)
    const defaultWidget = sortedDefaultLayout.find((defaultItem) =>
      currentWidget.id.includes(defaultItem.id),
    );

    if (defaultWidget) {
      const widgetType = defaultWidget.id;
      const expectedCount = widgetTypeCounts.get(widgetType) || 0;
      const currentCount = addedWidgetCounts.get(widgetType) || 0;

      // Only add if we haven't reached the expected count for this widget type
      if (currentCount < expectedCount) {
        updatedConfig.push({
          ...currentWidget,
          x: defaultWidget.x ?? 0,
          y: defaultWidget.y ?? 0,
          w: defaultWidget.w ?? 6,
          h: defaultWidget.h ?? 9,
        });
        addedWidgetCounts.set(widgetType, currentCount + 1);
      }
      // If we've already reached the expected count, this widget is a duplicate and is excluded
    }
    // Widgets that don't match any default layout item are excluded
  });

  // Find missing widgets from the default layout
  // Check if we need more widgets of each type
  const missingWidgets: Config[] = [];
  sortedDefaultLayout.forEach((defaultItem) => {
    const widgetType = defaultItem.id;
    const expectedCount = widgetTypeCounts.get(widgetType) || 0;
    const currentCount = addedWidgetCounts.get(widgetType) || 0;
    const needed = expectedCount - currentCount;

    // Add missing widgets up to the expected count
    for (let i = 0; i < needed; i++) {
      missingWidgets.push(defaultItem);
    }
  });

  // Add missing widgets
  missingWidgets.forEach((defaultItem) => {
    updatedConfig.push({
      id: `${defaultItem.id}-widget-${crypto.randomUUID()}`,
      x: defaultItem.x ?? 0,
      y: defaultItem.y ?? 0,
      w: defaultItem.w ?? 6,
      h: defaultItem.h ?? 9,
      isDynamic: false,
    });
  });

  return updatedConfig;
};
